import React, { FC, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { routes } from '../../../../constants/routes'
import { icons } from '../../../../constants/icons'
import { columns } from './tableColumnsList'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { TableComponent } from '../../../../components/Table'
import { Modal } from '../../../../components/Modal'
import { getRiskScoreRulesRequest, deleteRiskScoreRuleRequest } from '../../../../store/actions'
import { getCrmRulesList, riskScoreRulesLoading } from '../../../../store/selectors'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    paddingTop: theme.spacing(4),
    '& p': {
      display: 'none',
    },
  },
  rightSide: {
    width: '100%',
    flexWrap: 'nowrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    '& :first-child': {
      marginLeft: 0,
    },
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(3),
  },
}))

export const RiskScoreRules: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const { tab } = useParams<{ tab: string }>()
  const { search } = useLocation()
  const page = new URLSearchParams(search).get('page')

  const { data } = useSelector(getCrmRulesList)
  const isLoading = useSelector(riskScoreRulesLoading)

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [ruleIdToDelete, setRuleIdToDelete] = useState<number | null>(null)

  const formMethods = useForm({
    mode: 'onBlur',
  })

  useEffect(() => {
    dispatch(getRiskScoreRulesRequest({ page: Number(page) || 1 }))
  }, [dispatch, page])

  const handlePageChange = (pageNumber: number) => {
    history.push(`${routes.SETTINGS_RULES}/${tab}?page=${pageNumber}`)
  }

  const handleInfoClick = (id: string | number | undefined) => {
    history.push(`${routes.SETTINGS_RULES}/${tab}/${id}/details`)
  }

  const handleEditClick = (id: string | number | undefined) => {
    history.push(`${routes.SETTINGS_RULES}/${tab}/${id}/edit`)
  }

  const openDeleteModal = (id: string | number) => {
    setRuleIdToDelete(Number(id))
    setDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setRuleIdToDelete(null)
    setDeleteModalOpen(false)
  }

  const handleConfirmDelete = () => {
    if (ruleIdToDelete) {
      dispatch(deleteRiskScoreRuleRequest({ id: ruleIdToDelete }))
      closeDeleteModal()
    }
  }

  return (
    <Grid container className={classes.topWrap}>
      <FormProvider {...formMethods}>
        <Grid container className={classes.rightSide}>
          <GreenButton
            width={120}
            prefix={icons.ADD}
            ml={8}
            text={'Create Rule'}
            href={`${routes.SETTINGS_RULES}/${tab}/create`}
          />
        </Grid>
      </FormProvider>

      <Grid container className={classes.table}>
        <TableComponent
          columns={columns}
          // eslint-disable-next-line
          data={data.items as any}
          params={data.meta}
          loadData={handlePageChange}
          onInfo={handleInfoClick}
          onEdit={handleEditClick}
          onDelete={openDeleteModal}
          getElementId={handleInfoClick}
          loading={isLoading}
          isShowInfoIcon
          isShowEditIcon
          threeAction
        />
      </Grid>
      <Modal
        isOpen={isDeleteModalOpen}
        title="Delete Rule"
        modalClose={closeDeleteModal}
        onSubmit={handleConfirmDelete}
      >
        <Typography>Are you sure you want to delete this rule?</Typography>
        <Typography>This action cannot be undone.</Typography>
      </Modal>
    </Grid>
  )
}
